import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const COLOR = "#EF8200";
const TOOLS = [["arrow", "Arrow"], ["circle", "Circle"], ["box", "Box"], ["text", "Text"]];

function drawShape(ctx, s, lw) {
  ctx.strokeStyle = COLOR; ctx.fillStyle = COLOR; ctx.lineWidth = lw; ctx.lineCap = "round"; ctx.lineJoin = "round";
  const { x1, y1, x2, y2 } = s;
  if (s.type === "box") { ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1)); return; }
  if (s.type === "circle") {
    ctx.beginPath();
    ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, Math.abs(x2 - x1) / 2 || 1, Math.abs(y2 - y1) / 2 || 1, 0, 0, Math.PI * 2);
    ctx.stroke(); return;
  }
  if (s.type === "arrow") {
    const ang = Math.atan2(y2 - y1, x2 - x1); const head = lw * 4.5;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - head * Math.cos(ang - Math.PI / 6), y2 - head * Math.sin(ang - Math.PI / 6));
    ctx.lineTo(x2 - head * Math.cos(ang + Math.PI / 6), y2 - head * Math.sin(ang + Math.PI / 6));
    ctx.closePath(); ctx.fill(); return;
  }
  if (s.type === "text") {
    const fs = lw * 6;
    ctx.font = `800 ${fs}px sans-serif`; ctx.textBaseline = "middle";
    const w = ctx.measureText(s.text).width; const pad = fs * 0.35;
    ctx.fillRect(x1 - pad, y1 - fs / 2 - pad, w + pad * 2, fs + pad * 2);
    ctx.fillStyle = "#fff"; ctx.fillText(s.text, x1, y1);
  }
}

// Draw arrows, circles, boxes and text labels on an FAQ screenshot; saves a new flattened picture.
export default function ImageAnnotator({ src, open, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [tool, setTool] = useState("arrow");
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const lw = () => Math.max(4, Math.round((imgRef.current?.naturalWidth || 800) / 160));

  useEffect(() => {
    if (!open) return;
    setShapes([]); setDraft(null); setReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { imgRef.current = img; setReady(true); };
    img.onerror = () => toast.error("Couldn't load that picture");
    img.src = src;
  }, [open, src]);

  useEffect(() => {
    const c = canvasRef.current; const img = imgRef.current;
    if (!ready || !c || !img) return;
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const ctx = c.getContext("2d");
    ctx.drawImage(img, 0, 0);
    [...shapes, ...(draft ? [draft] : [])].forEach((s) => drawShape(ctx, s, lw()));
  }, [ready, shapes, draft]);

  const pt = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * c.width, y: ((e.clientY - r.top) / r.height) * c.height };
  };
  const down = (e) => {
    const p = pt(e);
    if (tool === "text") {
      const text = window.prompt("Label text (e.g. 'Tap here')");
      if (text && text.trim()) setShapes((s) => [...s, { type: "text", x1: p.x, y1: p.y, text: text.trim().slice(0, 40) }]);
      return;
    }
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDraft({ type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const move = (e) => { if (draft) { const p = pt(e); setDraft((d) => ({ ...d, x2: p.x, y2: p.y })); } };
  const up = () => {
    if (draft && (Math.abs(draft.x2 - draft.x1) > 4 || Math.abs(draft.y2 - draft.y1) > 4)) setShapes((s) => [...s, draft]);
    setDraft(null);
  };

  const save = () => {
    setBusy(true);
    canvasRef.current.toBlob(async (blob) => {
      try {
        const form = new FormData();
        form.append("file", blob, "faq-highlight.png");
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        onSaved(data.url);
        toast.success("Highlighted picture ready — remember to Save the FAQ");
        onClose();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't save the picture");
      } finally { setBusy(false); }
    }, "image/png");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-rf-cream max-h-[95dvh] overflow-y-auto" data-testid="image-annotator">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black text-rf-ink">Highlight the screenshot</DialogTitle>
          <DialogDescription>Pick a tool, then drag on the picture (tap for Text).</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 mb-2">
          {TOOLS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTool(k)} data-testid={`annotate-tool-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-bold border ${tool === k ? "bg-rf-amber border-rf-orange" : "bg-white border-rf-ink/15"}`}>{label}</button>
          ))}
          <button type="button" onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length} data-testid="annotate-undo"
            className="px-4 py-2 rounded-full text-sm font-bold border bg-white border-rf-ink/15 disabled:opacity-40">Undo</button>
          <button type="button" onClick={() => setShapes([])} disabled={!shapes.length} data-testid="annotate-clear"
            className="px-4 py-2 rounded-full text-sm font-bold border bg-white border-rf-ink/15 disabled:opacity-40">Clear</button>
        </div>
        {ready ? (
          <canvas ref={canvasRef} data-testid="annotate-canvas"
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
            className="block mx-auto max-w-full max-h-[65dvh] w-auto h-auto rounded-xl border border-rf-ink/10 cursor-crosshair touch-none bg-white" />
        ) : <p className="py-16 text-center text-rf-ink-soft">Loading picture…</p>}
        <div className="flex justify-end gap-2 mt-3">
          <button type="button" onClick={onClose} className="btn-outline-ink !py-2 !px-4 text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !shapes.length || !ready} data-testid="annotate-save"
            className="btn-amber !py-2 !px-4 text-sm disabled:opacity-50">{busy ? "Saving…" : "Use highlighted picture"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
